import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Email as EmailIcon,
  Autorenew as AutorenewIcon,
  CheckCircle as CheckCircleIcon,
  Shield as ShieldIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import colors from '../theme/colors';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Simulate sending reset link
    setSuccess(true);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Circles */}
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          top: -200,
          right: -200,
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          bottom: -150,
          left: -150,
          filter: 'blur(60px)',
        }}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          maxWidth: 1400,
          margin: '0 auto',
          width: '100%',
          p: 4,
          gap: 4,
          zIndex: 1,
        }}
      >
        {/* Left Side - Animation */}
        <MotionBox
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
          }}
        >
          {/* Animated Reset Icons */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 500,
              height: 400,
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Center Rotating Refresh Icon */}
            <motion.div
              animate={{
                rotate: [0, 360],
                scale: [1, 1.15, 1],
              }}
              transition={{
                rotate: {
                  duration: 4,
                  repeat: Infinity,
                  ease: 'linear',
                },
                scale: {
                  duration: 2,
                  repeat: Infinity,
                },
              }}
              style={{
                position: 'absolute',
                zIndex: 3,
              }}
            >
              <Box
                sx={{
                  width: 130,
                  height: 130,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                }}
              >
                <AutorenewIcon sx={{ fontSize: 70, color: 'white' }} />
              </Box>
            </motion.div>

            {/* Flying Email Icons in Figure-8 Pattern */}
            <motion.div
              animate={{
                x: [0, 100, 0, -100, 0],
                y: [0, -80, 0, 80, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              style={{
                position: 'absolute',
                zIndex: 2,
              }}
            >
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              >
                <Box
                  sx={{
                    width: 55,
                    height: 55,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(255, 255, 255, 0.3)',
                  }}
                >
                  <SendIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
              </motion.div>
            </motion.div>

            {/* Diagonal Moving Shield Icon */}
            <motion.div
              animate={{
                x: [-120, 120],
                y: [-120, 120],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut',
              }}
              style={{
                position: 'absolute',
                zIndex: 2,
              }}
            >
              <motion.div
                animate={{
                  scale: [1, 1.3, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              >
                <Box
                  sx={{
                    width: 55,
                    height: 55,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(255, 255, 255, 0.3)',
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 28, color: 'white' }} />
                </Box>
              </motion.div>
            </motion.div>

            {/* Circular Orbiting Check Icon */}
            <motion.div
              animate={{
                rotate: -360,
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{
                position: 'absolute',
                width: 350,
                height: 350,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <motion.div
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                >
                  <Box
                    sx={{
                      width: 55,
                      height: 55,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 28, color: 'white' }} />
                  </Box>
                </motion.div>
              </Box>
            </motion.div>

            {/* Multiple Pulsing Background Circles */}
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
              }}
              style={{
                position: 'absolute',
                width: 280,
                height: 280,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 1,
              }}
            />
            <motion.div
              animate={{
                scale: [1.3, 1, 1.3],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
              }}
              style={{
                position: 'absolute',
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
                zIndex: 0,
              }}
            />
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, textAlign: 'center' }}>
            Reset Your Password
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, textAlign: 'center', maxWidth: 500 }}>
            Enter your email address and we'll send you a link to reset your password
          </Typography>
        </MotionBox>

        {/* Right Side - Forgot Password Form */}
        <MotionBox
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MotionPaper
            elevation={24}
            sx={{
              p: 5,
              borderRadius: 4,
              width: '100%',
              maxWidth: 480,
              backgroundColor: 'white',
            }}
          >
            {/* Logo Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 2,
                  boxShadow: '0 8px 24px rgba(75, 73, 172, 0.3)',
                }}
              >
                <Typography variant="h3" sx={{ color: 'white', fontWeight: 800 }}>
                  H
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: colors.text.primary }}>
                Forgot Password?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No worries, we'll send you reset instructions
              </Typography>
            </Box>

            {/* Success or Error Alert */}
            {success ? (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                Reset link sent! Check your email for further instructions.
              </Alert>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            ) : null}

            {!success ? (
              <>
                {/* Forgot Password Form */}
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: colors.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                      boxShadow: '0 4px 16px rgba(75, 73, 172, 0.4)',
                      '&:hover': {
                        boxShadow: '0 6px 24px rgba(75, 73, 172, 0.5)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Send Reset Link
                  </Button>
                </form>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.primary.main,
                      cursor: 'pointer',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={() => navigate('/signin')}
                  >
                    ← Back to Sign In
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/signin')}
                  sx={{
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: 'none',
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.main,
                      background: 'rgba(75, 73, 172, 0.05)',
                    },
                  }}
                >
                  Return to Sign In
                </Button>
              </Box>
            )}
          </MotionPaper>
        </MotionBox>
      </Box>
    </Box>
  );
}

export default ForgotPassword;
